import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useClientQueries } from '@zenstackhq/tanstack-query/react';
import { MessageCircle, X, Send, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

import { schema } from '../__generated__/schema-lite';
import { useAuth } from '../auth/AuthContext';
import { translateError } from '../lib/translate-error';

type NavigateTo = { path: string; label: string } | null;
type ChatMessage = { role: 'user' | 'assistant'; content: string; navigateTo?: NavigateTo };

/** Floating in-app help assistant. A bubble bottom-right opens a small chat
 *  panel that answers questions about the app (via the `assistantAsk`
 *  procedure) and can offer a button to navigate to the relevant page. */
export function AssistantWidget() {
  const { t, i18n } = useTranslation();
  const { token } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const crud = useClientQueries(schema);
  const ask = crud.$procs.assistantAsk.useMutation();

  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages, ask.isPending]);

  // Only offer help to signed-in users.
  if (!token) return null;

  const suggestions = t('assistant.suggestions', { returnObjects: true }) as string[];

  async function send(question: string) {
    const q = question.trim();
    if (!q || ask.isPending) return;
    const history = messages.map((m) => ({ role: m.role, content: m.content }));
    setMessages((prev) => [...prev, { role: 'user', content: q }]);
    setInput('');
    try {
      const raw = (await ask.mutateAsync({
        args: { input: { question: q, history, language: i18n.language, path: pathname } },
      })) as string;
      let reply = raw;
      let navigateTo: NavigateTo = null;
      try {
        const parsed = JSON.parse(raw) as { reply: string; navigateTo: NavigateTo };
        reply = parsed.reply;
        navigateTo = parsed.navigateTo;
      } catch {
        /* fall back to raw text */
      }
      setMessages((prev) => [...prev, { role: 'assistant', content: reply, navigateTo }]);
    } catch (err) {
      toast.error(translateError(t, err));
    }
  }

  function goTo(nav: NonNullable<NavigateTo>) {
    navigate(nav.path);
    setOpen(false);
  }

  return (
    <>
      {/* Bubble */}
      <button
        type="button"
        aria-label={t('assistant.title')}
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-4 right-4 z-30 flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg transition hover:opacity-90"
      >
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
      </button>

      {/* Panel */}
      {open ? (
        <div className="fixed bottom-20 right-4 z-30 flex h-[32rem] max-h-[80vh] w-[22rem] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-xl border border-border bg-card shadow-2xl">
          <header className="flex items-center gap-2 border-b border-border px-4 py-3">
            <MessageCircle className="h-4 w-4 text-primary" />
            <span className="text-sm font-semibold">{t('assistant.title')}</span>
          </header>

          <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-3">
            {messages.length === 0 ? (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">{t('assistant.greeting')}</p>
                <div className="flex flex-col gap-2">
                  {suggestions.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => send(s)}
                      className="rounded-lg border border-border px-3 py-2 text-left text-sm hover:bg-muted"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              messages.map((m, i) => (
                <div key={i} className={m.role === 'user' ? 'text-right' : 'text-left'}>
                  <div
                    className={
                      m.role === 'user'
                        ? 'inline-block rounded-lg bg-primary px-3 py-2 text-sm text-primary-foreground'
                        : 'inline-block rounded-lg bg-muted px-3 py-2 text-sm'
                    }
                  >
                    {m.content}
                  </div>
                  {m.navigateTo ? (
                    <div className="mt-1">
                      <button
                        type="button"
                        onClick={() => goTo(m.navigateTo!)}
                        className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
                      >
                        {m.navigateTo.label || t('assistant.go')}
                        <ArrowRight className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ) : null}
                </div>
              ))
            )}
            {ask.isPending ? (
              <div className="text-left">
                <div className="inline-block rounded-lg bg-muted px-3 py-2 text-sm text-muted-foreground">
                  {t('assistant.thinking')}
                </div>
              </div>
            ) : null}
          </div>

          <form
            className="flex items-center gap-2 border-t border-border px-3 py-3"
            onSubmit={(e) => {
              e.preventDefault();
              send(input);
            }}
          >
            <input
              className="input flex-1"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t('assistant.placeholder')}
              disabled={ask.isPending}
            />
            <button
              type="submit"
              aria-label={t('assistant.send')}
              disabled={ask.isPending || !input.trim()}
              className="btn-primary flex h-9 w-9 items-center justify-center p-0 disabled:opacity-50"
            >
              <Send className="h-4 w-4" />
            </button>
          </form>
        </div>
      ) : null}
    </>
  );
}
